
/* eslint-disable @typescript-eslint/no-unsafe-type-assertion -- for now*/

// src/services/messaging/messaging.service.impl.ts

import type { HeliaNode } from "@/types/poc/helia";
import type { LibP2pWithServices } from "@/types/poc/libp2p";
import type { MessageHandler, MessagePayload } from "@/types/poc/message";
import type { Libp2pService } from "@/services/abstract/poc/libp2p.service.interface";
import { type FileUploadedResult, type FilesUpdatedResult, type FileClonedResult, type FileUploadedAcknowledgedResult, type CheckForKeysNeedingRegeneratingResult, type FileReplicationStartedResult, type FileReplicationProgressResult, type FileReplicationFailedResult, MessagingService, type MessageHandlerResult, type MessageStats } from "@/services/abstract/poc/messaging.service.interface";
import { FILE_TRANSFER_TOPIC, TOPIC_LIST_TOPIC } from "@/config/networkConstants";
import type { LoggerService } from "@/services/abstract/LoggerService";
import type { Logger } from "@/types/Logger";

/**
 * Concrete implementation of the MessagingService.
 * This class orchestrates message handling without any side effects.
 */
export class MessagingServiceImpl extends MessagingService {
  private readonly log: Logger;
  private topics: string[] = [];

  constructor(
    loggerService: LoggerService,
    private readonly libp2pService: Libp2pService,
    private readonly handlers: MessageHandler[],
    private readonly sixId: string
  ) {
    super();
    this.log = loggerService.createLogger({ namespace: 'six3ro:p2p:messaging' });
  }

  setupSubscriptions(
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null,
    onMessage: (result: MessageHandlerResult, stats: MessageStats) => void,
    topics: string[]
  ): () => void {
    const unsubscribeFunctions: Array<() => void> = [];
    this.topics = topics;
    this.log.info(`Setting up subscriptions for topics: ${topics.join(', ')}`);
    // Subscribe to each topic
    for (const topic of topics) {
      // Only log file-list topic subscriptions (critical for monitoring)
      if (topic.includes('file-list')) {
        this.log.info(`📡 SUBSCRIBED TO: ${topic}`);
      }

      const unsubscribe = this.libp2pService.subscribeToTopic(
        libp2pNode,
        topic,
(data: Uint8Array) => {
  if (heliaNode === null) {
    this.log.warn("heliaNode is null, cannot handle incoming message.");
    return;
  }
  void handleIncomingMessage(data, topic, libp2pNode, heliaNode);
}      );

      unsubscribeFunctions.push(unsubscribe);
    }

    this.log.info(`✅ Subscribed to ${topics.length} topics`);
const handleIncomingMessage = async(
  data: Uint8Array,
  topic: string,
  libp2pNode: LibP2pWithServices,
  heliaNode: HeliaNode
) =>{
  try {
    const decodedMessage = new TextDecoder().decode(data);
    this.log.debug(`Message received on topic ${topic}: ${decodedMessage}`);
    // Try to parse the message
    let parsed: any;
    try {
      parsed = JSON.parse(decodedMessage);
    } catch {
      return;
    }

    const sender = parsed.sender ?? parsed.from ?? parsed.peerId ?? 'unknown';
    const timestamp = parsed.timestamp ?? Date.now();
    const messageType = parsed.type ?? 'unknown';
    // LOG: MESH HEARTBEAT from HAP - critical diagnostic info
    if (topic.includes('mesh-heartbeat') && parsed.source === 'hap') {
      this.log.debug(`💓 MESH HEARTBEAT [HAP→Browser] RECEIVED!`);
      this.log.debug(`   Seq: ${parsed.seq}`);
      this.log.debug(`   HAP PeerId: ${parsed.peerId?.substring(0, 16) ?? 'unknown'}...`);
      this.log.debug(`   HAP Connections: ${parsed.connections}`);
      this.log.debug(`   HAP MeshTotal: ${parsed.meshTotal}`);
      // if (parsed.meshTotal === 0) {
      //   this.log.warn(`   ⚠️  HAP MESH IS EMPTY!`);
      // }
      return; // Don't process further, just log
    }

    const message = parsed as MessagePayload;

    // Extract stats before processing (for all messages)
    const stats: MessageStats = {
      fromPeer: sender,
      topic,
      timestamp,
      messageType,
      messageContent: decodedMessage // Include raw message content for all messages
    };

    const result = await this.processMessage(topic, message, libp2pNode, heliaNode);

    if (result !== null) {
      onMessage(result, stats);
    }
  } catch (error) {
    this.log.error(`❌ Error processing message on topic ${topic}:`, error);
  }
}

    // Return cleanup function
    return () => {
      this.log.info('🧹 Cleaning up all messaging subscriptions');
      unsubscribeFunctions.forEach(fn => { fn(); });
    };
  }

  async processMessage(
    topic: string,
    message: MessagePayload,
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null
  ): Promise<MessageHandlerResult | null> {
    const myPeerId = libp2pNode.peerId.toString();

    // Skip our own messages
    if (message.sender === myPeerId) {
      this.log.info('Skipping our own message');
      return null;
    }

    // Check if message is targeted and meant for us
    if (message.targetPeerId != null && message.targetPeerId !== "" && message.targetPeerId !== myPeerId) {
      return null;
    }

    // Find appropriate handler
    const handler = this.handlers.find(h => h.canHandle(topic, message));
    if (handler === undefined) {
      this.log.debug(`No handler found for message type ${message.type} on topic ${topic}`);
      return null;
    }

    // Process message with handler (pure function - returns data)
    const data = await handler.process(message, libp2pNode, heliaNode);

    switch (message.type) {
      case 'file-ready-for-download':
        return { type: 'file-ready-for-download', data: data as FileUploadedResult };
      case 'files-updated':
        return { type: 'files-updated', data: data as FilesUpdatedResult };
      case 'file-cloned':
        return { type: 'file-cloned', data: data as FileClonedResult };
      case 'file-ready-for-download-acknowledged':
        return { type: 'file-ready-for-download-acknowledged', data: data as FileUploadedAcknowledgedResult };
      case 'file-key-ready-for-download-acknowledged':
        return { type: 'file-key-ready-for-download-acknowledged', data: data as FileUploadedAcknowledgedResult };
      case 'check-for-keys-needing-regenerating':
        return { type: 'check-for-keys-needing-regenerating', data: data as CheckForKeysNeedingRegeneratingResult };
      case 'file-replication-started':
        return { type: 'file-replication-started', data: data as FileReplicationStartedResult };
      case 'file-replication-progress':
        return { type: 'file-replication-progress', data: data as FileReplicationProgressResult };
      case 'file-replication-failed':
        return { type: 'file-replication-failed', data: data as FileReplicationFailedResult };
      default:
        this.log.warn(`Unhandled message type: ${message.type}`);
        return null;
    }
  }

  async publishMessage(
    libp2pNode: LibP2pWithServices,
    topic: string,
    message: MessagePayload
  ): Promise<void> {
    // Add sender information
    const enrichedMessage: MessagePayload = {
      ...message,
      sender: libp2pNode.peerId.toString(),
      timestamp: Date.now()
    };

    const data = JSON.stringify(enrichedMessage);
    await this.libp2pService.publishToTopic(libp2pNode, topic, data);
  }

  async publishTopicList(
    libp2pNode: LibP2pWithServices,
    sixId: string,
    topics: string[]
  ): Promise<void> {
    // Publish to a per-six topic so each six has its own topic list
    const topicListTopic = TOPIC_LIST_TOPIC;;

    const topicListMessage: MessagePayload = {
      type: 'topic-list',
      id: crypto.randomUUID(),
      sender: libp2pNode.peerId.toString(),
      timestamp: Date.now(),
      topics,
      sixId
    };

    this.log.info(`📢 Publishing topic list for ${sixId}: ${topics.join(', ')}`);
    const data = JSON.stringify(topicListMessage);
    await this.libp2pService.publishToTopic(libp2pNode, topicListTopic, data);
  }

  getSupportedTopics(): string[] {
    return [...this.topics];
  }
}
